import { ProcessingStage, VoiceInteractionResult } from '../models';
import WebSocketService from './WebSocketService';

interface VoiceProcessingData {
  audioData: string;
  audioFormat: string;
  conversationId?: string;
}

class VoiceService {
  private webSocketService: WebSocketService;

  constructor(webSocketService: WebSocketService) {
    this.webSocketService = webSocketService;
  }

  /**
   * Process a voice interaction through the WebSocket service
   * 
   * @param audioData Base64 encoded audio data
   * @param audioFormat Audio format (e.g., 'webm', 'wav')
   * @param conversationId Optional conversation ID for context
   * @param onProgress Optional callback for progress updates
   * @returns Promise resolving to voice interaction result
   */
  public async processVoiceInteraction(
    audioData: string,
    audioFormat: string,
    conversationId?: string,
    onProgress?: (stage: ProcessingStage) => void
  ): Promise<VoiceInteractionResult> {
    // Prepare the data for voice processing
    const data: VoiceProcessingData = {
      audioData,
      audioFormat,
      conversationId
    };

    try {
      // Send the request and handle the response
      const result = await this.webSocketService.sendMessage<VoiceInteractionResult, VoiceProcessingData>(
        'processVoice',
        data,
        onProgress,
        60000 // 60 second timeout for voice processing
      );
      
      // Validate the response
      if (!result.transcription || !result.chatResponse || !result.speechResponse) {
        throw new Error('Incomplete voice processing result');
      }
      
      return result;
    } catch (error) {
      console.error('Voice processing failed:', error);
      throw error;
    }
  }
}

export default VoiceService; 